(function () {
    'use strict';

    angular
        .module('elogbooksDirectives')
        .directive('elogbooksSelectMultipleHtmlOptions', function (lodash, $translate) {
            return {
                restrict: 'A',
                transclude: true,
                templateUrl: '/modules/directives/form-elements/select-multiple-html-options/select-multiple-html-options.html',
                scope: {
                    model: '=ngModel',
                    loading: '=',
                    autoLoadNextPageOnEmpty: '=?',
                    autoReloadWhenEmptied: '=?',
                    forceLoadAllPages: '=?'
                },
                require: ['ngModel'],
                link: function ($scope, $element, $attrs, controller, $transclude) {
                    $scope.model = angular.extend({
                        selected: [],
                        options: [],
                        hasMoreOptions: false, // if true - would trigger loading more when user reaches the end of the list
                        loadNext: null, // async function to load more results
                    }, $scope.model);

                    $scope.isFocused = false;
                    $scope.loadNextSpinnerVisible = false;
                    $scope.isLoadingMore = false;
                    $scope.lastClickedIndex = null;
                    $scope.isMouseDown = false;
                    $scope.activeIndex = null;
                    $scope.options = [];

                    // Set the default value for autoLoadNextPageOnEmpty if not provided
                    $scope.autoLoadNextPageOnEmpty = $scope.autoLoadNextPageOnEmpty || false;

                    $scope.autoReloadWhenEmptied = $scope.autoReloadWhenEmptied || false;

                    // Add tabindex to the main element to support focus, keyboard navigation and remove outline effect
                    $element.attr('tabindex', '0').css('outline', 'none');

                    let $listContent = $element.find('.select-multiple-html-options');
                    $listContent.html('');

                    let autoLoadNextPage = null;

                    // Create outside click listener to detect focus on an element
                    const onDocumentClick = (e) => $scope.$apply(() => $scope.isFocused = $element[0].contains(e.target));
                    document.addEventListener('click', onDocumentClick);
                    $scope.$on('$destroy', () => document.removeEventListener('click', onDocumentClick));

                    // Build an option list and watch on any changes
                    $scope.$watch('model.options', (options) => {
                        if (!options) return;

                        const oldSelected = $scope.model.selected || [];
                        let selectedOptions = [];
                        $scope.model.selected = [];
                        $scope.options = [];
                        $listContent.empty();

                        // Create document fragment for better performance
                        const fragment = document.createDocumentFragment();

                        options.forEach((item) => {
                            // Check if this option was previously selected
                            item.selected = !!oldSelected.find((oldOption) => oldOption.id === item.id);

                            // Create a new scope for this option
                            const $itemScope = $scope.$new();
                            Object.assign($itemScope, item);
                            $scope.options.push($itemScope);

                            $transclude($itemScope, function(clone) {
                                // Create option element
                                const optionElement = document.createElement('li');
                                optionElement.setAttribute('role', 'option');
                                optionElement.classList.add('select-multiple-html-option');

                                if ($itemScope.optionContainerStyle) {
                                    const styles = angular.isArray($itemScope.optionContainerStyle)
                                        ? $itemScope.optionContainerStyle
                                        : $itemScope.optionContainerStyle.split(' ');

                                    styles.forEach(cssClass => {
                                        const trimmedClass = cssClass.trim();
                                        if (trimmedClass) {
                                            optionElement.classList.add(trimmedClass);
                                        }
                                    });
                                }

                                const $optionWrapper = angular.element(optionElement);
                                $itemScope.$optionWrapper = $optionWrapper;

                                // Add event handlers
                                $optionWrapper.on('click', (e) => $scope.onOptionClick(e, $itemScope, $optionWrapper));
                                $optionWrapper.on('$destroy', () => $itemScope.$destroy());
                                $optionWrapper.on('mousedown', (e) => e.preventDefault()); // Disable drag & drop

                                // Append content and add to fragment
                                $optionWrapper.append(clone);
                                fragment.appendChild(optionElement);

                                // Pre-select option if needed
                                if ($itemScope.selected) {
                                    $optionWrapper.addClass('option-selected');
                                    $optionWrapper.attr('aria-selected', 'true');
                                    selectedOptions.push($itemScope);
                                }
                            });
                        });

                        // Append all options at once for better performance
                        $listContent[0].appendChild(fragment);
                        $scope.model.selected = selectedOptions;

                        if (options.length === 0 && $scope.autoReloadWhenEmptied && $scope.model.hasMoreOptions && autoLoadNextPage) {
                            autoLoadNextPage();
                        }
                    });

                    // Add load more spinner if the loadNext function is provided
                    if ($scope.model.loadNext) {
                        const $optionsContainer = $element.find('.options-container');
                        const loadMoreSpinner = document.createElement('div');
                        loadMoreSpinner.setAttribute('role', 'option');
                        loadMoreSpinner.classList.add('load-more-spinner');
                        loadMoreSpinner.innerHTML = '<span class="load-more-content">' +
                            $translate.instant('LOADING_MORE') + ' ' +
                            '<span><cafm-icon name="spinner" pulse></cafm-icon></span>' +
                        '</span>';

                        // Hide spinner if no more options (last page)
                        if (!$scope.model.hasMoreOptions) {
                            loadMoreSpinner.classList.add('no-more');
                        }

                        $optionsContainer.append(loadMoreSpinner);

                        // Create IntersectionObserver to detect when LOADING_MORE element is visible
                        const loadMoreObserver = new IntersectionObserver((entries) => {
                            const isVisible = entries[0].isIntersecting && !loadMoreSpinner.classList.contains('no-more');

                            if ($scope.$root.$$phase) {
                                $scope.loadNextSpinnerVisible = isVisible;
                            } else {
                                $scope.$apply(() => {
                                    $scope.loadNextSpinnerVisible = isVisible;
                                });
                            }

                            // If visible and not already loading, load more items
                            // When forceLoadAllPages is enabled, paging is driven by the runner, so skip observer-triggered loads
                            if (isVisible && !$scope.isLoadingMore && !$scope.forceLoadAllPages) {
                                loadNextPage();
                            }
                        }, {
                            root: $optionsContainer[0],
                            threshold: 0.1
                        });

                        // Start observing the spinner
                        loadMoreObserver.observe(loadMoreSpinner);

                        // Cleanup observer on destruction
                        $scope.$on('$destroy', () => loadMoreObserver.disconnect());

                        // Function to load the next page of results
                        const loadNextPage = () => {
                            if ($scope.isLoadingMore || !$scope.model.hasMoreOptions) {
                                return Promise.resolve();
                            }

                            $scope.isLoadingMore = true;

                            return $scope.model.loadNext().then(() => {
                                // If no more options, hide the spinner
                                if (!$scope.model.hasMoreOptions) {
                                    loadMoreSpinner.classList.add('no-more');
                                }

                                if ($scope.$root.$$phase) {
                                    $scope.isLoadingMore = false;
                                } else {
                                    $scope.$apply(() => {
                                        $scope.isLoadingMore = false;
                                    });
                                }

                                // Reset the observer so it can fire again if still visible
                                loadMoreObserver.unobserve(loadMoreSpinner);
                                loadMoreObserver.observe(loadMoreSpinner);
                            }).catch((e) => {
                                loadMoreSpinner.classList.add('no-more');

                                if ($scope.$root.$$phase) {
                                    $scope.isLoadingMore = false;
                                } else {
                                    $scope.$apply(() => {
                                        $scope.isLoadingMore = false;
                                    });
                                }

                                console.error('Load more results error.', e);
                            });
                        };

                        // Force load-all runner
                        const runForceLoadAll = () => {
                            if (!$scope.forceLoadAllPages) return;
                            if (!$scope.model.hasMoreOptions) return;
                            loadNextPage().finally(() => {
                                // Continue chaining as long as flag is on and more pages exist
                                setTimeout(runForceLoadAll, 0);
                            });
                        };

                        // Kick off immediately if requested
                        if ($scope.forceLoadAllPages) {
                            runForceLoadAll();
                        }

                        // React when the flag toggles on
                        $scope.$watch('forceLoadAllPages', (val) => {
                            if (val) {
                                runForceLoadAll();
                            }
                        });

                        autoLoadNextPage = loadNextPage;

                        // Watch for changes to hasMoreOptions
                        $scope.$watch('model.hasMoreOptions', (hasMore) => {
                            if (hasMore) {
                                loadMoreSpinner.classList.remove('no-more');
                            } else {
                                loadMoreSpinner.classList.add('no-more');
                            }
                        });
                    }
                },
                controller: function ($scope, $element, lodash) {
                    const $listContainer = $element.find('.options-container');
                    const $listContent = $listContainer.find('.select-multiple-html-options');

                    let isDragging = false;
                    let startDragIndex = null;

                    // Debounced scope update for better performance
                    const updateScope = lodash.debounce(() => {
                        if (!$scope.$root.$$phase) {
                            $scope.$apply();
                        }
                    }, 10);

                    const selectOption = ($itemScope) => {
                        if (!$itemScope || $itemScope.selected) return;

                        $itemScope.selected = true;
                        $itemScope.$optionWrapper.addClass('option-selected');
                        $itemScope.$optionWrapper.attr('aria-selected', 'true');
                        $scope.model.selected.push($itemScope);
                    };

                    const removeSelection = ($itemScope) => {
                        if (!$itemScope || !$itemScope.selected) return;

                        $itemScope.$optionWrapper.removeClass('option-selected');
                        $itemScope.$optionWrapper.removeAttr('aria-selected');
                        $itemScope.selected = false;

                        const index = $scope.model.selected.indexOf($itemScope);
                        if (index > -1) {
                            $scope.model.selected.splice(index, 1);
                        }
                    };

                    const selectRange = (startIndex, endIndex) => {
                        const start = Math.min(startIndex, endIndex);
                        const end = Math.max(startIndex, endIndex);

                        for (let i = start; i <= end; i++) {
                            if (i >= 0 && i < $scope.options.length) {
                                selectOption($scope.options[i]);
                            }
                        }

                        $scope.activeIndex = endIndex;
                    };

                    const clearAllSelections = () => {
                        $scope.options.forEach(option => removeSelection(option));
                    };

                    $scope.onOptionClick = (event, $itemScope) => {
                        if ($scope.loading) return;

                        const currentIndex = $scope.options.findIndex(opt => opt.id === $itemScope.id);
                        if (currentIndex === -1) return;

                        // Ctrl/Mac Cmd key - toggle selection
                        if (event.ctrlKey || event.metaKey) {
                            if ($itemScope.selected) {
                                removeSelection($itemScope);
                            } else {
                                selectOption($itemScope);
                                $scope.activeIndex = currentIndex;
                            }
                        }
                        // Shift key - select range
                        else if (event.shiftKey && $scope.lastClickedIndex !== null) {
                            selectRange($scope.lastClickedIndex, currentIndex);
                        }
                        // Default - select only this option
                        else {
                            clearAllSelections();
                            selectOption($itemScope);
                            $scope.activeIndex = currentIndex;
                        }

                        $scope.lastClickedIndex = currentIndex;
                        $scope.isFocused = true;
                        $element.focus();
                        updateScope();
                    };

                    // Mouse drag and release events handling
                    $listContainer.on('mousedown', (event) => {
                        if ($scope.loading || event.button !== 0) return; // only left mouse button

                        $scope.isMouseDown = true;

                        const target = angular.element(event.target).closest('.select-multiple-html-option');
                        if (target.length === 0) return;

                        const optionIndex = $listContent.find('.select-multiple-html-option').index(target);

                        if (optionIndex !== -1) {
                            startDragIndex = optionIndex;
                            isDragging = true;

                            $element.focus();
                            $scope.isFocused = true;
                        }
                    });

                    // Handle mouse movement for drag selection
                    $listContent.on('mousemove', (event) => {
                        if ($scope.loading || !isDragging) return;

                        const target = angular.element(event.target).closest('.select-multiple-html-option');
                        if (target.length === 0) return;

                        const optionIndex = $listContent.find('.select-multiple-html-option').index(target);

                        if (optionIndex !== -1 && startDragIndex !== null) {
                            // Clear selection if not using modifier keys
                            if (!(event.ctrlKey || event.metaKey)) {
                                clearAllSelections();
                            }

                            selectRange(startDragIndex, optionIndex);

                            $scope.isFocused = true;
                            $element.focus();
                            updateScope();
                        }
                    });

                    /**
                     * Handle keyboard shortcuts (Ctrl+A to select all)
                     */
                    const keydownHandler = (event) => {
                        if ($scope.loading || !$scope.isFocused) return;

                        // Ctrl+A or Cmd+A (select all)
                        if ((event.ctrlKey || event.metaKey) && event.key === 'a') {
                            event.preventDefault();
                            $scope.options.forEach(option => selectOption(option));
                            updateScope();
                        }
                    };

                    const mouseUpHandler = () => {
                        if ($scope.loading) return;

                        $scope.isMouseDown = false;

                        if (isDragging) {
                            isDragging = false;
                            startDragIndex = null;
                        }
                    };

                    // Register global event handlers
                    document.addEventListener('keydown', keydownHandler);
                    document.addEventListener('mouseup', mouseUpHandler);

                    $element.on('keydown', (event) => {
                        if ($scope.loading || $scope.isMouseDown) return;

                        const isArrowUp = event.key === 'ArrowUp';
                        const isArrowDown = event.key === 'ArrowDown';

                        if (!(isArrowDown || isArrowUp)) return;

                        // Determine current active index
                        const currentActiveIndex = $scope.activeIndex !== null ?
                            $scope.activeIndex :
                            $scope.options.findIndex(opt => opt.selected);

                        if (currentActiveIndex === -1) return;

                        // Calculate new index
                        const newActiveIndex = isArrowUp ? currentActiveIndex - 1 : currentActiveIndex + 1;

                        // Check if new index is valid
                        if (newActiveIndex < 0 || newActiveIndex >= $scope.options.length) return;

                        $scope.activeIndex = newActiveIndex;
                        const optionToSelect = $scope.options[newActiveIndex];

                        if (event.shiftKey) {
                            // Shift key - extend or reduce selection
                            if (optionToSelect.selected) {
                                removeSelection($scope.options[currentActiveIndex]);
                            } else {
                                selectOption(optionToSelect);
                            }
                        } else {
                            // No modifier - select only this option
                            clearAllSelections();
                            selectOption(optionToSelect);
                        }

                        // Scroll the selected option into view
                        if (optionToSelect.$optionWrapper && optionToSelect.$optionWrapper[0]) {
                            optionToSelect.$optionWrapper[0].scrollIntoView({
                                behavior: 'smooth',
                                block: 'nearest',
                            });
                        }

                        event.preventDefault();
                        $scope.isFocused = true;
                        $element.focus();
                        updateScope();
                    });

                    // Disable drag & drop
                    $listContent.on('mousedown', (e) => e.preventDefault());

                    // Handle focus events
                    const updateFocus = (isFocused) => {
                        if ($scope.$root.$$phase) {
                            $scope.isFocused = isFocused;
                        } else {
                            $scope.$apply(() => $scope.isFocused = isFocused);
                        }
                    };

                    $element.on('focusout', () => updateFocus(false));
                    $element.on('focus', () => updateFocus(true));

                    // Clean up event handlers on destroy
                    $scope.$on('$destroy', () => {
                        document.removeEventListener('keydown', keydownHandler);
                        document.removeEventListener('mouseup', mouseUpHandler);
                    });
                }
            }
        });
})();
